import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ───────────────────────────────────────────────────────────────

export type AssetStatus = "online" | "offline" | "maintenance" | "decommissioned";
export type AssetCriticality = "critical" | "normal";
export type AssetType = "bottling" | "infrastructure" | "safety" | "wine_production" | "vehicle" | "tools" | "fluids" | "tank";

export interface Asset {
  id: string;
  org_id: string;
  parent_id: string | null;
  facility_id: string | null;
  zone_id: string | null;
  name: string;
  description: string | null;
  asset_type: AssetType | null;
  status: AssetStatus;
  criticality: AssetCriticality;
  make: string | null;
  model: string | null;
  serial_number: string | null;
  year_installed: number | null;
  image_url: string | null;
  notes: string | null;
  maintainx_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  updated_by_name: string | null;
  // Joined fields from view
  facility_name?: string;
  zone_name?: string;
  parent_name?: string;
  open_wo_count?: number;
}

export interface CreateAssetInput {
  name: string;
  description?: string;
  parent_id?: string | null;
  facility_id?: string | null;
  zone_id?: string | null;
  asset_type?: AssetType | null;
  status?: AssetStatus;
  criticality?: AssetCriticality;
  make?: string;
  model?: string;
  serial_number?: string;
  year_installed?: number;
  notes?: string;
}

export interface UpdateAssetInput {
  name?: string;
  description?: string | null;
  parent_id?: string | null;
  facility_id?: string | null;
  zone_id?: string | null;
  asset_type?: AssetType | null;
  status?: AssetStatus;
  criticality?: AssetCriticality;
  make?: string | null;
  model?: string | null;
  serial_number?: string | null;
  year_installed?: number | null;
  image_url?: string | null;
  notes?: string | null;
  updated_by_name?: string | null;
}

// ── Queries ─────────────────────────────────────────────────────────────

export function useAssets(filters?: {
  status?: AssetStatus;
  asset_type?: AssetType;
  facility_id?: string;
  search?: string;
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["assets", filters],
    queryFn: async () => {
      let query = supabase
        .from("asset" as any)
        .select("*")
        .eq("is_active", true)
        .order("name", { ascending: true });

      if (filters?.status) query = query.eq("status", filters.status);
      if (filters?.asset_type) query = query.eq("asset_type", filters.asset_type);
      if (filters?.facility_id) query = query.eq("facility_id", filters.facility_id);
      if (filters?.search) query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Asset[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

export function useAsset(id: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["assets", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset" as any)
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as Asset;
    },
    enabled: !!id && isAuthenticated && !authLoading,
  });
}

export function useAssetParts(assetId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["asset-parts", assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_part" as any)
        .select("*, part:part_id(*)")
        .eq("asset_id", assetId!);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!assetId && isAuthenticated && !authLoading,
  });
}

export function useAssetVendors(assetId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["asset-vendors", assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_vendor" as any)
        .select("*, vendor:vendor_id(*)")
        .eq("asset_id", assetId!);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!assetId && isAuthenticated && !authLoading,
  });
}

export function useChildAssets(parentId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["assets", "children", parentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset" as any)
        .select("*")
        .eq("parent_id", parentId!)
        .eq("is_active", true)
        .order("name", { ascending: true });
      if (error) throw error;
      return (data || []) as Asset[];
    },
    enabled: !!parentId && isAuthenticated && !authLoading,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useCreateAsset() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAssetInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("asset" as any)
        .insert([{
          org_id: userData.org_id,
          name: input.name,
          description: input.description || null,
          parent_id: input.parent_id || null,
          facility_id: input.facility_id || null,
          zone_id: input.zone_id || null,
          asset_type: input.asset_type || null,
          status: input.status || "online",
          criticality: input.criticality || "normal",
          make: input.make || null,
          model: input.model || null,
          serial_number: input.serial_number || null,
          year_installed: input.year_installed || null,
          notes: input.notes || null,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as Asset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      toast({ title: "Asset created", description: "The asset has been added to the registry." });
    },
    onError: (error: Error) => {
      toast({ title: "Error creating asset", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateAsset() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateAssetInput }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: ua } = await supabase
        .from("user_account" as any)
        .select("display_name")
        .eq("external_auth_user", user!.id)
        .single();
      const updatedByName = (ua as any)?.display_name || user?.email || null;

      const { data, error } = await supabase
        .from("asset" as any)
        .update({ ...input, updated_by_name: updatedByName })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Asset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      toast({ title: "Asset updated", description: "The asset has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error updating asset", description: error.message, variant: "destructive" });
    },
  });
}

export function useDuplicateAsset() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (asset: Asset) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: ua } = await supabase
        .from("user_account" as any)
        .select("org_id, display_name")
        .eq("external_auth_user", user!.id)
        .single();

      const { data, error } = await supabase
        .from("asset" as any)
        .insert({
          org_id: (ua as any).org_id,
          name: `${asset.name} (Copy)`,
          description: asset.description,
          asset_type: asset.asset_type,
          status: "online",
          criticality: asset.criticality,
          facility_id: asset.facility_id,
          zone_id: asset.zone_id,
          make: asset.make,
          model: asset.model,
          notes: asset.notes,
          updated_by_name: (ua as any)?.display_name || user?.email || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Asset;
    },
    onSuccess: (newAsset) => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      toast({ title: "Asset duplicated", description: `"${newAsset.name}" created.` });
    },
    onError: (error: Error) => {
      toast({ title: "Error duplicating asset", description: error.message, variant: "destructive" });
    },
  });
}

export function useArchiveAsset() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("asset" as any)
        .update({ is_active: false })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Asset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      toast({ title: "Asset archived", description: "The asset has been archived." });
    },
    onError: (error: Error) => {
      toast({ title: "Error archiving asset", description: error.message, variant: "destructive" });
    },
  });
}
